import { useState, useEffect, useCallback, useMemo } from "react";
import { useUser } from "@/context/UserContext";
import { supabase } from "@/lib/supabase";
import type { ClockInRecord } from "@/types/clockIn";

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("zh-TW", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    weekday: "short",
  });
}

function formatTime(dateStr: string): string {
  return new Date(dateStr).toLocaleTimeString("zh-TW", {
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDuration(ms: number): string {
  const hours = Math.floor(ms / 3600000);
  const minutes = Math.floor((ms % 3600000) / 60000);
  return `${hours}小時 ${minutes}分鐘`;
}

interface DailyRecord {
  date: string;
  clockIn: ClockInRecord | null;
  clockOut: ClockInRecord | null;
  duration: number;
  workLog: string | null;
}

export default function ClockInRecordPage() {
  const { userRole, userName, employeeId, isAdmin } = useUser();
  const [records, setRecords] = useState<ClockInRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterEmployee, setFilterEmployee] = useState<string>("all");
  const [filterDateFrom, setFilterDateFrom] = useState<string>("");
  const [filterDateTo, setFilterDateTo] = useState<string>("");
  const [employees, setEmployees] = useState<{ id: string; name: string; employee_code: string }[]>([]);

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      let query = supabase
        .from("clock_in_records")
        .select("*")
        .order("clock_time", { ascending: false });

      if (!isAdmin && employeeId) {
        query = query.eq("employee_id", employeeId);
      }

      if (filterDateFrom) {
        query = query.gte("clock_time", new Date(filterDateFrom).toISOString());
      }
      if (filterDateTo) {
        query = query.lte("clock_time", new Date(filterDateTo + "T23:59:59").toISOString());
      }

      const { data, error: queryError } = await query;

      if (queryError) {
        setError(queryError.message || "無法載入打卡紀錄");
      } else {
        setRecords(data || []);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "載入打卡紀錄時發生錯誤";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [employeeId, isAdmin, filterDateFrom, filterDateTo]);

  // Fetch employees list for admin filter
  const fetchEmployees = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const { data, error: empError } = await supabase
        .from("employees")
        .select("id, name, employee_code")
        .order("name");
      if (!empError && data) {
        setEmployees(data);
      }
    } catch {
      // ignore
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchRecords();
    fetchEmployees();
  }, [fetchRecords, fetchEmployees]);

  // Filter records by employee (admin only)
  const filteredRecords = useMemo(() => {
    if (!isAdmin || filterEmployee === "all") return records;
    return records.filter((r) => r.employee_id === filterEmployee);
  }, [records, isAdmin, filterEmployee]);

  // Group records by date
  const dailyRecords = useMemo((): DailyRecord[] => {
    const map = new Map<string, { clockIn: ClockInRecord | null; clockOut: ClockInRecord | null; workLog: string | null }>();

    filteredRecords.forEach((r) => {
      const date = r.clock_time.split("T")[0];
      const existing = map.get(date) || { clockIn: null, clockOut: null, workLog: null };
      if (r.type === "clock_in") {
        existing.clockIn = r;
        if (r.work_log) existing.workLog = r.work_log;
      } else {
        existing.clockOut = r;
      }
      map.set(date, existing);
    });

    const sortedDates = Array.from(map.keys()).sort((a, b) => b.localeCompare(a));

    return sortedDates.map((date) => {
      const entry = map.get(date)!;
      let duration = 0;
      if (entry.clockIn && entry.clockOut) {
        duration = new Date(entry.clockOut.clock_time).getTime() - new Date(entry.clockIn.clock_time).getTime();
      }
      return {
        date,
        clockIn: entry.clockIn,
        clockOut: entry.clockOut,
        duration,
        workLog: entry.workLog,
      };
    });
  }, [filteredRecords]);

  // Statistics
  const stats = useMemo(() => {
    const totalDays = dailyRecords.length;
    const completeDays = dailyRecords.filter((d) => d.clockIn && d.clockOut).length;
    const totalWorkMs = dailyRecords.reduce((sum, d) => sum + d.duration, 0);
    const avgWorkMs = completeDays > 0 ? totalWorkMs / completeDays : 0;
    const lateCount = dailyRecords.filter((d) => {
      if (!d.clockIn) return false;
      const hour = new Date(d.clockIn.clock_time).getHours();
      const minute = new Date(d.clockIn.clock_time).getMinutes();
      return hour > 9 || (hour === 9 && minute > 0);
    }).length;
    return { totalDays, completeDays, totalWorkMs, avgWorkMs, lateCount };
  }, [dailyRecords]);

  // Unique employees in current records (for admin summary)
  const uniqueEmployees = useMemo(() => {
    const map = new Map<string, { name: string; code: string; count: number }>();
    filteredRecords.forEach((r) => {
      const existing = map.get(r.employee_id);
      if (existing) {
        existing.count += 1;
      } else {
        map.set(r.employee_id, { name: r.employee_name, code: r.employee_code, count: 1 });
      }
    });
    return Array.from(map.entries()).map(([id, data]) => ({ id, ...data }));
  }, [filteredRecords]);

  const clearFilters = () => {
    setFilterDateFrom("");
    setFilterDateTo("");
    setFilterEmployee("all");
  };

  return (
    <div className="space-y-5 max-w-[1440px] mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-foreground-900" style={{ fontFamily: "var(--font-heading)" }}>
          打卡紀錄
        </h1>
        {isAdmin && (
          <span className="text-xs bg-primary-100 text-primary-700 px-2.5 py-1 rounded-full font-medium">
            超級管理員模式
          </span>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <div className="bg-background-50 border border-background-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-foreground-500">總計天數</span>
            <div className="w-7 h-7 rounded-lg bg-primary-100 flex items-center justify-center">
              <i className="ri-calendar-check-line text-primary-500 text-xs" />
            </div>
          </div>
          <p className="text-lg font-bold text-foreground-900">{stats.totalDays}</p>
          <span className="text-xs text-foreground-400">天</span>
        </div>
        <div className="bg-background-50 border border-background-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-foreground-500">完整打卡</span>
            <div className="w-7 h-7 rounded-lg bg-primary-100 flex items-center justify-center">
              <i className="ri-check-double-line text-primary-500 text-xs" />
            </div>
          </div>
          <p className="text-lg font-bold text-foreground-900">{stats.completeDays}</p>
          <span className="text-xs text-foreground-400">天（上下班均有）</span>
        </div>
        <div className="bg-background-50 border border-background-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-foreground-500">平均工時</span>
            <div className="w-7 h-7 rounded-lg bg-secondary-100 flex items-center justify-center">
              <i className="ri-time-line text-secondary-500 text-xs" />
            </div>
          </div>
          <p className="text-lg font-bold text-foreground-900">{formatDuration(stats.avgWorkMs)}</p>
          <span className="text-xs text-foreground-400">每天</span>
        </div>
        <div className="bg-background-50 border border-background-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-foreground-500">遲到次數</span>
            <div className="w-7 h-7 rounded-lg bg-accent-100 flex items-center justify-center">
              <i className="ri-alarm-warning-line text-accent-500 text-xs" />
            </div>
          </div>
          <p className="text-lg font-bold text-foreground-900">{stats.lateCount}</p>
          <span className="text-xs text-foreground-400">次（9:00後打卡）</span>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-background-50 border border-background-200 rounded-xl p-4">
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-end">
          {isAdmin && (
            <div className="flex flex-col gap-1.5 w-full sm:w-auto">
              <label className="text-xs text-foreground-500">員工篩選</label>
              <select
                value={filterEmployee}
                onChange={(e) => setFilterEmployee(e.target.value)}
                className="px-3 py-2 rounded-lg border border-background-200 text-sm text-foreground-900 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400 min-w-[180px]"
              >
                <option value="all">全部員工</option>
                {employees.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.name} ({emp.employee_code})
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="flex flex-col gap-1.5 w-full sm:w-auto">
            <label className="text-xs text-foreground-500">開始日期</label>
            <input
              type="date"
              value={filterDateFrom}
              onChange={(e) => setFilterDateFrom(e.target.value)}
              className="px-3 py-2 rounded-lg border border-background-200 text-sm text-foreground-900 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>
          <div className="flex flex-col gap-1.5 w-full sm:w-auto">
            <label className="text-xs text-foreground-500">結束日期</label>
            <input
              type="date"
              value={filterDateTo}
              onChange={(e) => setFilterDateTo(e.target.value)}
              className="px-3 py-2 rounded-lg border border-background-200 text-sm text-foreground-900 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            <button
              onClick={fetchRecords}
              className="px-4 py-2 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 cursor-pointer whitespace-nowrap"
            >
              查詢
            </button>
            <button
              onClick={clearFilters}
              className="px-4 py-2 rounded-lg bg-background-100 text-foreground-600 text-sm font-medium hover:bg-background-200 cursor-pointer whitespace-nowrap"
            >
              清除
            </button>
          </div>
        </div>
      </div>

      {/* Admin: Employee Summary */}
      {isAdmin && uniqueEmployees.length > 0 && (
        <div className="bg-background-50 border border-background-200 rounded-xl p-4">
          <h3 className="text-sm font-semibold text-foreground-900 mb-3">員工打卡摘要</h3>
          <div className="flex flex-wrap gap-2">
            {uniqueEmployees.map((emp) => (
              <button
                key={emp.id}
                onClick={() => setFilterEmployee(emp.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer transition-colors ${
                  filterEmployee === emp.id
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-600 hover:bg-background-200"
                }`}
              >
                {emp.name} ({emp.code}) · {emp.count} 筆
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="bg-background-50 border border-background-200 rounded-xl p-12 text-center">
          <i className="ri-loader-4-line animate-spin text-primary-500 text-2xl" />
          <p className="text-sm text-foreground-500 mt-3">載入打卡紀錄中...</p>
        </div>
      )}

      {/* Error */}
      {error && !loading && (
        <div className="bg-background-50 border border-background-200 rounded-xl p-8 text-center">
          <div className="w-12 h-12 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-3">
            <i className="ri-error-warning-line text-accent-500 text-xl" />
          </div>
          <p className="text-sm text-foreground-500">{error}</p>
          <button
            onClick={fetchRecords}
            className="mt-3 px-4 py-2 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 cursor-pointer"
          >
            重試
          </button>
        </div>
      )}

      {/* Records List */}
      {!loading && !error && (
        <>
          {dailyRecords.length === 0 ? (
            <div className="bg-background-50 border border-background-200 rounded-xl p-12 text-center">
              <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-4">
                <i className="ri-time-line text-foreground-400 text-2xl" />
              </div>
              <h2 className="text-lg font-semibold text-foreground-900 mb-2">
                暫無打卡紀錄
              </h2>
              <p className="text-sm text-foreground-500">
                {isAdmin ? "目前沒有符合篩選條件的打卡紀錄" : "您尚未有任何打卡紀錄，請前往首頁進行打卡"}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {dailyRecords.map((day) => (
                <div key={day.date} className="bg-background-50 border border-background-200 rounded-xl overflow-hidden">
                  {/* Date Header */}
                  <div className="px-4 py-3 border-b border-background-100 flex items-center justify-between bg-background-100/50">
                    <div className="flex items-center gap-2">
                      <i className="ri-calendar-line text-primary-500 text-sm" />
                      <span className="text-sm font-semibold text-foreground-900">
                        {formatDate(day.date)}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      {day.clockIn && day.clockOut && (
                        <span className="text-xs text-foreground-500">
                          工時 {formatDuration(day.duration)}
                        </span>
                      )}
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        day.clockIn && day.clockOut
                          ? "bg-primary-50 text-primary-700"
                          : day.clockIn
                            ? "bg-secondary-50 text-secondary-700"
                            : "bg-accent-50 text-accent-700"
                      }`}>
                        {day.clockIn && day.clockOut
                          ? "已完成"
                          : day.clockIn
                            ? "未下班"
                            : "未打卡"}
                      </span>
                    </div>
                  </div>

                  {/* Record Details */}
                  <div className="p-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {/* Clock In */}
                      <div className={`flex items-start gap-3 p-3 rounded-lg ${
                        day.clockIn ? "bg-primary-50/60" : "bg-background-100/60"
                      }`}>
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                          day.clockIn ? "bg-primary-100" : "bg-background-200"
                        }`}>
                          <i className={`ri-login-circle-line ${day.clockIn ? "text-primary-500" : "text-foreground-400"} text-sm`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-foreground-500 mb-0.5">上班打卡</p>
                          <p className={`text-sm font-semibold ${day.clockIn ? "text-foreground-900" : "text-foreground-400"}`}>
                            {day.clockIn ? formatTime(day.clockIn.clock_time) : "—"}
                          </p>
                          {day.clockIn && (
                            <p className="text-xs text-foreground-400 mt-0.5">
                              員工: {day.clockIn.employee_name} ({day.clockIn.employee_code})
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Clock Out */}
                      <div className={`flex items-start gap-3 p-3 rounded-lg ${
                        day.clockOut ? "bg-foreground-50/60" : "bg-background-100/60"
                      }`}>
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                          day.clockOut ? "bg-foreground-100" : "bg-background-200"
                        }`}>
                          <i className={`ri-logout-circle-r-line ${day.clockOut ? "text-foreground-600" : "text-foreground-400"} text-sm`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-foreground-500 mb-0.5">下班打卡</p>
                          <p className={`text-sm font-semibold ${day.clockOut ? "text-foreground-900" : "text-foreground-400"}`}>
                            {day.clockOut ? formatTime(day.clockOut.clock_time) : "—"}
                          </p>
                          {day.clockOut && (
                            <p className="text-xs text-foreground-400 mt-0.5">
                              員工: {day.clockOut.employee_name} ({day.clockOut.employee_code})
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Work Log */}
                    {day.workLog && (
                      <div className="mt-3 pt-3 border-t border-background-100">
                        <div className="flex items-start gap-2">
                          <i className="ri-file-text-line text-foreground-400 text-xs mt-0.5" />
                          <div>
                            <p className="text-xs text-foreground-500 mb-1">工作內容</p>
                            <p className="text-sm text-foreground-700 leading-relaxed">{day.workLog}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}